/**
 * Requirements entry point on the project's Work Items tab.
 *
 * The epic defines requirements as work items owned by the project and asks
 * that every role can see the project's work items; until this panel existed
 * the tab rendered a placeholder list that never had any data in it, and the
 * requirement screens had no inbound link from a project at all.
 *
 * The count comes from the same `GET /requirements` the lifecycle screen uses
 * (direct to the Go backend, session cookie), asked for with `limit=1` because
 * only `total` is needed here.
 */

import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography, makeStyles } from '@material-ui/core';
import { InfoCard, Progress } from '@backstage/core-components';
import Alert from '@material-ui/lab/Alert';
import { Link as RouterLink } from 'react-router-dom';
import { listThreads } from '../requirement-lifecycle/api';
import { ACTIVE_PROJECT_QUERY_PARAM } from '../activeProject';

const useStyles = makeStyles(theme => ({
  count: {
    marginBottom: theme.spacing(2),
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
}));

export interface ProjectRequirementsPanelProps {
  /** Project id as it appears on the project entity (a stringified int64). */
  projectId: string;
  /**
   * Preview mode runs without the Go backend, so the count is skipped and only
   * the navigation is offered.
   */
  isPreviewMode?: boolean;
}

export const ProjectRequirementsPanel: FC<ProjectRequirementsPanelProps> = ({
  projectId,
  isPreviewMode = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('projects');
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(!isPreviewMode);
  const [failed, setFailed] = useState(false);

  const numericProjectId = Number(projectId);
  const usableProjectId =
    Number.isSafeInteger(numericProjectId) && numericProjectId > 0
      ? numericProjectId
      : null;

  useEffect(() => {
    if (isPreviewMode || usableProjectId === null) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    void (async () => {
      try {
        const result = await listThreads({
          projectId: usableProjectId,
          limit: 1,
        });
        if (!cancelled) setTotal(result.total);
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [usableProjectId, isPreviewMode]);

  // Deep-link with the project already selected, so the requirement screens do
  // not fall back to whatever the tab last remembered.
  const target = (path: string) =>
    usableProjectId === null
      ? path
      : `${path}?${ACTIVE_PROJECT_QUERY_PARAM}=${usableProjectId}`;

  return (
    <InfoCard title={t('metrics.requirementsTitle')}>
      {loading && <Progress />}
      {!loading && failed && (
        <Box className={classes.count}>
          <Alert severity="warning">{t('activeProject.loadError')}</Alert>
        </Box>
      )}
      {!loading && !failed && total !== null && (
        <Typography variant="body2" className={classes.count}>
          {t('details.requirementCount', { count: total })}
        </Typography>
      )}
      <div className={classes.actions}>
        <Button
          color="primary"
          variant="contained"
          component={RouterLink}
          to={target('/requirements')}
          disabled={usableProjectId === null}
        >
          {t('activeProject.openLifecycle')}
        </Button>
        <Button
          color="primary"
          variant="outlined"
          component={RouterLink}
          to={target('/requirement-doctor')}
          disabled={usableProjectId === null}
        >
          {t('activeProject.openDoctor')}
        </Button>
      </div>
    </InfoCard>
  );
};

export default ProjectRequirementsPanel;
